/// <amd-module name="Dashboards/WidgetCatalog/WidgetContributionsHelper" />
import * as Resources from "Dashboards/WidgetCatalog/Resources";
import { ContributionComponentProps, WidgetContributionRenderingProps } from "Dashboards/WidgetContracts/Internal/WidgetContributionTypes";
import { getExtensionMarketplaceUrl } from "VSS/Ext/ExternalContentUtil";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureEnabled, isFeatureFlagEnabled } from "VSS/Platform/Feature";

export async function resolveWidgetContributionAsync(
    pageContext: IVssPageContext,
    contributionId: string
): Promise<WidgetContributionRenderingProps> {
    if (!contributionId) {
        throw new Error(Resources.WidgetContributionIdMissing);
    }

    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    let contributionProperties = await contributionService.getContributionAsync<WidgetContributionRenderingProps>(contributionId);
    if (contributionProperties) {
        contributionProperties = patchContribution(pageContext, contributionId, contributionProperties);
        return contributionProperties;
    } else {
        throw new Error(Resources.WidgetContributionCouldNotLoad);
    }
}

/** Attempts to asynchronously load the Contribution for rendering New Platform Component */
export async function resolveComponentContributionAsync(
    pageContext: IVssPageContext,
    widgetComponentTypeId: string
): Promise<ContributionComponentProps | null> {
    if (!widgetComponentTypeId) {
        throw new Error(Resources.WidgetComponentContributionIdMissing);
    }

    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const componentContributionProperties = await contributionService.getContributionAsync<ContributionComponentProps>(widgetComponentTypeId);
    if (componentContributionProperties && componentContributionProperties.componentType && componentContributionProperties.includes) {
        return (await {
            componentType: componentContributionProperties.componentType,
            includes: componentContributionProperties.includes
        }) as ContributionComponentProps;
    } else {
        return null; // Indicates consumers should use fallback implementations
    }
}

export interface ProviderInfo {
    /** Name of the contribution provider (aka extension). */
    providerDisplayName: string;
    /** Pointer to the contribution provider (aka extension) on the marketplace */
    marketplaceUrl: string;
}

export function getProviderInfo(pageContext: IVssPageContext, contributionId: string): Promise<ProviderInfo> {
    const unknownContributionProvider = "UnknownContributionProvider";
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const contributionProvider = contributionService.getContributionProvider(contributionId) || {
        id: "Unknown",
        displayName: unknownContributionProvider
    };

    return getExtensionMarketplaceUrl(pageContext, contributionProvider.id).then((marketplaceUrl: string) => {
        const contributionProviderDisplayName = contributionProvider != null ? contributionProvider.displayName : unknownContributionProvider;
        return {
            providerDisplayName: contributionProviderDisplayName,
            marketplaceUrl: marketplaceUrl
        };
    });
}

/**
 * An internal widget (a.k.a. first-party widget) is a widget developed internally at Microsoft
 * and published using the 'ms' publisher.
 */
export function isInternalContribution(contributionId: string) {
    return contributionId.startsWith("ms.vss");
}

export function contributionHasModernImplementation(pageContext: IVssPageContext, contributionId: string): boolean {
    let contribution: WidgetContributionRenderingProps | null;
    try {
        contribution = resolveWidgetContribution(pageContext, contributionId);
    } catch {
        contribution = null;
    }

    return contribution != null && contribution.widgetComponentContributionId != null && !contribution.ignoreComponentContributionForDashboardLoad;
}

export function resolveWidgetContribution(pageContext: IVssPageContext, contributionId: string): WidgetContributionRenderingProps {
    if (!contributionId) {
        throw new Error(Resources.WidgetContributionIdMissing);
    }

    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    let contributionProperties = contributionService.getContribution<WidgetContributionRenderingProps>(contributionId);
    if (contributionProperties) {
        contributionProperties = patchContribution(pageContext, contributionId, contributionProperties);
        return contributionProperties;
    } else {
        throw new Error(Resources.WidgetContributionCouldNotLoad);
    }
}

/**
 * Performs a limited patch of Flag dependent properties. This is a subset of coverage handled in WidgetMetadataService, as required for widget Host rendering (As opposed to edit and catalog scenarios, which are backed by the catalog service proper).
 *  */
function patchContribution(
    pageContext: IVssPageContext,
    contributionId: string,
    contributionProps: WidgetContributionRenderingProps
): WidgetContributionRenderingProps {
    const legacySprintBurndownWidgetContribution = "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.SprintBurndownWidget";
    if (contributionId === legacySprintBurndownWidgetContribution) {
        if (isFeatureEnabled(pageContext, "ms.vss-dashboards-web.sprint-burndown-availability")) {
            // Allow Lightbox after upgrade.
            contributionProps.lightbox = { width: 900, height: 700, resizable: true };
        } else {
            // Catalog entry is populated with full range of sizes.
            contributionProps.supportedSizes = [
                {
                    rowSpan: 1,
                    columnSpan: 2
                }
            ];
            // Prevent showing the widget as configurable when the feature is off
            contributionProps.configComponentContributionId = undefined;
        }
    }
    return contributionProps;
}

/**
 * A widget is a slow widget if it is on the hard-coded list of slow widgets.
 * This list is based on observed TTI values of dashboards that consist of a single widget for each widget.
 */
export function widgetImplementationIsSlow(contributionId: string, pageContext: IVssPageContext): boolean {
    const delayLoadSlowWidgetsEnabled = isFeatureFlagEnabled(pageContext, "WebAccess.Dashboards.DelayLoadSlowWidgets.Enable", false);
    const slowWidgets = delayLoadSlowWidgetsEnabled ? ["ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.VelocityWidget"] : [];
    return slowWidgets.some(slowId => contributionId === slowId);
}
